import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllTeamMembers,
  deleteTeamMember,
  updateTeamOrder,
  addTeamMember,
  updateTeamMember,
} from "../../../../../../../services/teamServices";

import { uploadTeamPhoto } from "../../../../../../../services/teamPhotoServices";

import {
  buildTeamMemberPayload,
  calculateTeamStats,
  extractCreatedTeamMemberId,
  filterTeam,
  getNextTeamPosition,
  getTeamCategories,
  normalizeOrderTeam,
  sortTeamByPosition,
  validateImageFile,
  validateTeamMemberPayload,
} from "../utils/teamSettingsUtils";

export function useTeamSettings() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    query: "",
    categorie: "",
  });

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableMember, setEditableMember] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deletingId, setDeletingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllTeamMembers();
      setTeam(sortTeamByPosition(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement équipe :", error);
      toast.error("Impossible de charger les membres de l’équipe.");
      setTeam([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const filteredTeam = useMemo(() => {
    return filterTeam(team, filters);
  }, [team, filters]);

  const stats = useMemo(() => calculateTeamStats(team), [team]);

  const filteredStats = useMemo(
    () => calculateTeamStats(filteredTeam),
    [filteredTeam]
  );

  const categories = useMemo(() => getTeamCategories(team), [team]);

  const nextPosition = useMemo(() => getNextTeamPosition(team), [team]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
      categorie: "",
    });
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableMember(null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (member) => {
    setFormMode("edit");
    setEditableMember(member || null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditableMember(null);
    setFormMode("create");
    setUploadProgress(0);
  };

  const closeAfterSubmit = () => {
    setFormDrawerOpen(false);
    setEditableMember(null);
    setFormMode("create");
    setUploadProgress(0);
  };

  const handleSubmitTeamMember = async ({
    formData,
    file,
    outputFormat = "webp",
  }) => {
    const payload = buildTeamMemberPayload(formData);
    const payloadError = validateTeamMemberPayload(payload);
    const fileError = validateImageFile(file);

    if (payloadError) {
      toast.error(payloadError);
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    try {
      setSubmittingForm(true);
      setUploadProgress(0);

      if (formMode === "create") {
        const created = await addTeamMember(payload);
        const createdId = extractCreatedTeamMemberId(created);

        if (file && createdId) {
          await uploadTeamPhoto(createdId, file, {
            format: outputFormat,
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded * 100) / event.total));
            },
          });
        }

        if (file && !createdId) {
          toast.warning(
            "Le membre a été créé, mais l’identifiant retourné par l’API ne permet pas d’envoyer la photo automatiquement."
          );
        }

        toast.success("Membre ajouté avec succès.");
      } else {
        if (!editableMember?.id) {
          toast.error("Membre introuvable.");
          return;
        }

        await updateTeamMember(editableMember.id, payload);

        if (file) {
          await uploadTeamPhoto(editableMember.id, file, {
            format: outputFormat,
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded * 100) / event.total));
            },
          });
        }

        toast.success("Membre mis à jour avec succès.");
      }

      await fetchTeamMembers();
      closeAfterSubmit();
    } catch (error) {
      console.error("Erreur enregistrement membre :", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Erreur lors de l’enregistrement du membre."
      );
    } finally {
      setSubmittingForm(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteTeamMember = async (member) => {
    if (!member?.id) return;

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer ${member.prenom || ""} ${
          member.nom || ""
        } ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(member.id);

      await deleteTeamMember(member.id);

      toast.success("Membre supprimé avec succès.");
      await fetchTeamMembers();
    } catch (error) {
      console.error("Erreur suppression membre :", error);
      toast.error("Erreur lors de la suppression du membre.");
    } finally {
      setDeletingId(null);
    }
  };

  const persistOrder = async (orderedRows) => {
    await updateTeamOrder(orderedRows);
  };

  const applyReorder = async (rows) => {
    const orderedRows = normalizeOrderTeam(rows);

    try {
      setReordering(true);
      setTeam(orderedRows);

      await persistOrder(orderedRows);
      toast.success("Ordre de l’équipe mis à jour.");
      await fetchTeamMembers();
    } catch (error) {
      console.error("Erreur ordre équipe :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchTeamMembers();
    } finally {
      setReordering(false);
    }
  };

  const moveTeamMember = async (memberId, direction) => {
    if (reordering || loading) return;

    const rows = sortTeamByPosition(team);
    const index = rows.findIndex((member) => member.id === memberId);

    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= rows.length) return;

    const nextRows = [...rows];
    const current = nextRows[index];

    nextRows[index] = nextRows[targetIndex];
    nextRows[targetIndex] = current;

    await applyReorder(nextRows);
  };

  return {
    team,
    filteredTeam,
    loading,

    filters,
    setFilter,
    resetFilters,
    categories,

    stats,
    filteredStats,
    nextPosition,

    formDrawerOpen,
    formMode,
    editableMember,
    submittingForm,
    uploadProgress,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleSubmitTeamMember,

    deletingId,
    reordering,
    moveTeamMember,

    fetchTeamMembers,
    handleDeleteTeamMember,
  };
}